import React from "react";
import { Link, useLocation, useParams } from "react-router-dom";

type WorkflowStep = {
  id: string;
  label: string;
  matches: (pathname: string, companyId: string) => boolean;
  backTo?: (companyId: string, params: { employeeId?: string }) => string | null;
  nextTo?: (companyId: string, params: { employeeId?: string }) => string | null;
  backLabel?: string;
  nextLabel?: string;
};

function buildClerkTimesheetWorkflow(companyId: string): WorkflowStep[] {
  const base = `/owner/company/${companyId}/payroll/timesheets`;

  return [
    {
      id: "timesheet-overview",
      label: "Timesheets",
      matches: (pathname, currentCompanyId) =>
        pathname === `/owner/company/${currentCompanyId}/payroll/timesheets`,
      nextTo: (currentCompanyId) =>
        `/owner/company/${currentCompanyId}/payroll/timesheets/manual-entry`,
      nextLabel: "Manual entry",
    },
    {
      id: "manual-entry",
      label: "Manual Entry",
      matches: (pathname, currentCompanyId) =>
        pathname === `/owner/company/${currentCompanyId}/payroll/timesheets/manual-entry`,
      backTo: (currentCompanyId) =>
        `/owner/company/${currentCompanyId}/payroll/timesheets`,
      nextTo: (_currentCompanyId, params) =>
        params.employeeId
          ? `/owner/company/${companyId}/payroll/timesheets/review/${params.employeeId}`
          : null,
      backLabel: "Back to timesheets",
      nextLabel: "Employee review",
    },
    {
      id: "employee-review",
      label: "Employee Review",
      matches: (pathname, currentCompanyId) =>
        pathname.startsWith(
          `/owner/company/${currentCompanyId}/payroll/timesheets/review/`
        ),
      backTo: (currentCompanyId) =>
        `/owner/company/${currentCompanyId}/payroll/timesheets/manual-entry`,
      nextTo: (currentCompanyId) =>
        `/owner/company/${currentCompanyId}/payroll/timesheets/disputes`,
      backLabel: "Manual entry",
      nextLabel: "Disputes",
    },
    {
      id: "disputes",
      label: "Disputes",
      matches: (pathname, currentCompanyId) =>
        pathname === `/owner/company/${currentCompanyId}/payroll/timesheets/disputes`,
      backTo: (_currentCompanyId, params) =>
        params.employeeId
          ? `/owner/company/${companyId}/payroll/timesheets/review/${params.employeeId}`
          : `/owner/company/${companyId}/payroll/timesheets`,
      backLabel: "Back",
    },
  ];
}

export default function WorkflowPager() {
  const location = useLocation();
  const { companyId, employeeId } = useParams<{
    companyId: string;
    employeeId?: string;
  }>();

  if (!companyId) {
    return null;
  }

  const steps = buildClerkTimesheetWorkflow(companyId);
  const currentIndex = steps.findIndex((step) =>
    step.matches(location.pathname, companyId)
  );

  if (currentIndex === -1) {
    return null;
  }

  const currentStep = steps[currentIndex];
  const backHref =
    currentStep.backTo?.(companyId, { employeeId }) ?? null;
  const nextHref =
    currentStep.nextTo?.(companyId, { employeeId }) ?? null;

  return (
    <div className="mb-6 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <p className="text-[10px] font-black uppercase tracking-[0.35em] text-slate-400">
            Workflow
          </p>
          <p className="mt-1 text-sm font-semibold text-slate-900">
            {currentStep.label}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {backHref ? (
            <Link
              to={backHref}
              className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-black uppercase tracking-[0.2em] text-slate-700 transition hover:bg-slate-50"
            >
              {currentStep.backLabel ?? "Back"}
            </Link>
          ) : null}

          {nextHref ? (
            <Link
              to={nextHref}
              className="inline-flex items-center justify-center rounded-xl bg-brand-vault px-4 py-2 text-xs font-black uppercase tracking-[0.2em] text-white transition hover:opacity-90"
            >
              {currentStep.nextLabel ?? "Next"}
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
}